import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { CrewScheduleCanvas } from "./CrewScheduleCanvas";
import { Grid, styled } from "@mui/material";
import { IconButton } from "src/components/IconButton/IconButton";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import {
  IUser,
  IPosition,
  IUserAppSettings,
  showOnCrewScheduleByBase,
  IBidPackageNonFlyingAssignments,
} from "@vmosolutions/oc-interfaces";
import { CrewList } from "src/components/CrewSchedule/CrewList";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  gotCrewAction,
  expandCollapseAllCrewRows,
  removeFilteredCrew,
  getCrewScheduleData,
  removeWindowProperties,
  setFilteredCrew,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import moment from "moment-timezone";
import { ICrewMember, defaultCrewBase } from "src/redux/reducers/CrewScheduleReducer";
import CrewScheduleContextMenu from "src/components/CrewSchedule/ContextMenu/CrewScheduleContextMenu";
import { CrewScheduleHeader } from "./CrewScheduleHeader/CrewScheduleHeader";
import { IPositionOptions, ISavedCrewScheduleDisplayOptions } from "./CrewScheduleHeader/CrewFilterAndSort";
import { DialogModalWrapper, EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { TimelineTime } from "src/pages/FlightTimeline/TimelineTime";
import { debounce } from "lodash";
import { setCrewBase, setCrewGroupFilter } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import _ from "lodash";
import { clearViolationsFromRedux } from "src/API/CrewScheduleErrors";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { BaseTimelineGL } from "src/SharedGL/BaseTimelineGL";
import { Theme } from "@mui/material";
import { sortByDisplayOrder } from "src/utils";

/**
 * The displayOrder of the highest position a crew member holds, or undefined if they hold none.
 *
 * Guarded on length because reduce with no initial value throws "Reduce of empty array with no
 * initial value". A crew member with an empty positions array would take down any sort using the
 * comparator below, and that comparator is also used by the status bar summary.
 */
const highestPositionOrder = (user: IUser): number | undefined =>
  user?.crewInfo?.positions?.length
    ? user.crewInfo.positions.reduce((previousValue: IPosition, currentValue: IPosition) =>
        previousValue.displayOrder < currentValue.displayOrder ? previousValue : currentValue
      )?.displayOrder
    : undefined;

/**
 * To be passed into sort call and will sort users(crew) by their highest assigned position then their seniority.
 * Crew holding no positions sort last rather than throwing.
 * @param a - IUser (crewmember)
 * @param b - IUser (crewmember)
 * @returns sorting order
 */
export const sortCrewMembersByPositionThenSeniority = (a: IUser, b: IUser): any => {
  const aPosOrder = highestPositionOrder(a);
  const bPosOrder = highestPositionOrder(b);

  return (
    (aPosOrder ?? Number.MAX_SAFE_INTEGER) - (bPosOrder ?? Number.MAX_SAFE_INTEGER) ||
    (a?.crewInfo?.masterSeniority ?? Number.MAX_SAFE_INTEGER) -
      (b?.crewInfo?.masterSeniority ?? Number.MAX_SAFE_INTEGER)
  );
};

interface ICrewScheduleWindowProps {
  id: string;
  path: string[];
  setCloseWarning: (warning: string) => void;
  windowProps: object;
}

export interface IModalInfo {
  type: EventModalTypes;
  modalProps?: any;
}

const defaultModal: IModalInfo = {
  type: EventModalTypes.None,
  modalProps: null,
};

const OCCrewScheduleWindow = styled(Grid)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  display: "flex",
  flexGrow: 1,
  flexBasis: 0,
  flexDirection: "column",
  overflow: "hidden",
  "& .crew_sched_header": {
    width: "100%",
    display: "flex",
  },
  "& .crew_sched_body": {
    width: "calc(100% - 5px)",
    margin: "0px 5px 5px 0px",
    display: "flex",
    flexGrow: 1,
    overflow: "hidden",
    "& .expand_all_crew_row_btn": {
      margin: "4px",
      padding: 0,
      borderRadius: "3px",
      "& svg": { width: "20px", height: "20px" },
    },
    "& .bidding_nfa_type_selector": {
      margin: 0,
    },
    "& .crew_list": {},
  },
  "& .linked": {
    position: "absolute",
    zIndex: 100, // has to be set to under 1200 so that context menu opens above it.
    width: "24px",
    height: "24px",
    top: "5px",
    right: 0,
    borderRadius: "5px",
    alignItems: "center",
    overflow: "clip",
    "& .link_button": {
      padding: 0,
      borderRadius: "5px",
      "& svg": {
        margin: "0px 6px",
        width: "14px",
      },
    },
  },
}));

export const CrewScheduleWindow = (props: ICrewScheduleWindowProps): JSX.Element => {
  const { id, setCloseWarning, windowProps } = props;

  const dispatch = useDispatch();
  const crewSchedContainer = useRef<HTMLDivElement>();
  const crewListRef = useRef<HTMLDivElement>();
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const timeIntervalLoaded = useSelector((state: IRootState) => state.crewSchedule.timeIntervalLoaded);

  const crewPairings = useSelector((state: IRootState) => state.crewSchedule.crewPairings);
  const crewNFAs = useSelector((state: IRootState) => state.crewSchedule.crewNonFlyingAssignments);
  // Copy before sorting. Array.prototype.sort mutates, and this array is the redux-owned master
  // position list that every position selector in the client reads from, so sorting the selector
  // result directly reorders shared state as a side effect of rendering this screen.
  // Memoised because `positions` feeds dependency arrays below - a fresh array identity on every
  // render would re-run them continuously.
  const rawPositions: IPosition[] = useSelector((state: IRootState) => state.structures.customerStructure.positions);
  const positions: IPosition[] = useMemo(() => [...rawPositions].sort(sortByDisplayOrder), [rawPositions]);

  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const crewBase = windowProperties?.crewBase ?? defaultCrewBase;

  const searchedCrew = useSelector((state: IRootState) => state.crewSchedule.searchCrewMember[id]);
  const [crewPosition, setCrewPosition] = useState<IPositionOptions>(null);

  // const filteredCrew = useSelector((state: IRootState) => state.crewSchedule.filteredCrew[id]);
  const [tabValues, setTabValues] = useState<boolean | string>(false);
  const timelineContainer = useRef<HTMLDivElement>(null);

  const crewGroupFilter = useSelector((state: IRootState) => state.crewSchedule.groupFilter[id]);
  const [activeEventModal, setActiveEventModal] = useState<IModalInfo>(defaultModal);

  const clearModal = useCallback((type: EventModalTypes) => {
    setActiveEventModal({ type });
  }, []);

  // If assigned, bidding assignment mode is active.
  const bidSettings = useSelector((state: IRootState) => state.crewSchedule.biddingSettings);
  const selectedBidPackage = useSelector((state: IRootState) => state.bidding.selectedBidPackage);
  const bidAssignmentActive = !!selectedBidPackage?.bidPackage?._id && bidSettings?.enabled;
  const [selectedBidNFATypes, setSelectedBidNFATypes] = useState<IBidPackageNonFlyingAssignments[]>([]);
  // Return dynamic height for NFA type selector component based on how many are selected (so that crew list position matches crew sched gl canvas).
  const nfaRowHeight = useMemo(() => {
    return !selectedBidNFATypes?.length
      ? BaseTimelineGL.NFAcountRowHeight + BaseTimelineGL.biddingWindowBarHeight
      : BaseTimelineGL.NFAcountRowHeight * selectedBidNFATypes?.length + BaseTimelineGL.biddingWindowBarHeight;
  }, [selectedBidNFATypes]);

  const now = useMemo(() => Date.now(), []);

  /**
   * Calls which are made when Crew Schedule is mounted.
   */
  const { cancellablePromise } = useCancellablePromise();
  useEffect(() => {
    // Retrieve the last state for the violation checks from the db on first load
    const getViolationStatus = async (): Promise<void> => {
      await AmplifyAPIWrapper.Get("/violations/status", null, cancellablePromise);
    };
    getViolationStatus();
    //! We do not want the cancellablePromise hook to cause a re-render so leave it out of the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // function set in ChrewScheduleCanvas, used in TimelineTime to jump to current Time
  const [scrollToDate, setScrollToDate] = useState<(jumpDate: Date) => void>(null);

  // If timezone changes, make sure start and times
  const startOfMonth = useMemo(
    () =>
      moment
        .utc(now)
        //.tz(timelineTZ)
        .startOf("month")
        .subtract("12", "hours") // Remove dependency on timelineTz (but add buffer to loaded flights to account for possible timezone changes.)
        ?.valueOf(),
    [now]
  );
  const endOfMonth = useMemo(
    () =>
      moment
        .utc(now)
        //.tz(timelineTZ)
        .endOf("month")
        .add("12", "hours") // Remove dependency on timelineTz (but add buffer to loaded flights to account for possible timezone changes.)
        ?.valueOf(),
    [now]
  );

  //filter through positions to get get them in a format for selection menu
  const positionOptions: IPositionOptions[] = useMemo(() => {
    if (!positions || positions.length === 0) {
      return [];
    }
    const options = positions
      .map((position: IPosition) => {
        return {
          value: position.displayOrder,
          key: position.title,
          label: position.description,
          id: position._id,
        };
      })
      .sort((a, b) => a.value - b.value); //have them in display order

    //! Only shows all selection if crew group filter is applied.
    if (crewGroupFilter && crewGroupFilter.length > 0)
      options.unshift({ value: 0, key: "ALL", label: "ALL", id: undefined });

    return options;
  }, [crewGroupFilter, positions]);

  // If crew position isn't set and available options are loaded in, apply default.
  useEffect(() => {
    if (!crewPosition && positionOptions?.length > 0) {
      setCrewPosition(positionOptions[0]);
    }
  }, [crewPosition, positionOptions]);

  // Retrieve any saved display options from localstorage and set them in redux for this particular window id.
  const [savedFilters, setSavedFilters] = useState<ISavedCrewScheduleDisplayOptions>(null);
  const appSettings: IUserAppSettings | null = useSelector((state: IRootState) => state.authentication.appSettings);
  const crewScheduleFetchIntervalDays = useSelector(
    (state: IRootState) => state.UISettings.crewScheduleFetchIntervalDays
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSavedDisplayOptions = useCallback(
    debounce(async (windowId: string, dispatch: any, appSettings: IUserAppSettings | null): Promise<void> => {
      try {
        // const savedFiltersObj: ISavedCrewScheduleDisplayOptions = JSON.parse(
        //   localStorage.getItem("crew-schedule-filters")
        // );
        const savedFiltersObj = appSettings?.crewScheduleFilters;
        if (savedFiltersObj) {
          const { crewBase, groupFilter, position } = savedFiltersObj;
          if (crewBase) dispatch(setCrewBase(crewBase, windowId));
          if (groupFilter) dispatch(setCrewGroupFilter(groupFilter, windowId));
          if (position) setCrewPosition(position);
          setSavedFilters(savedFiltersObj);
        } //else throw new Error("Bad filter/sort saved setting.");
        // crewScheduleFilters have a default value of null so this
        //would throw an error unless the user actually selected a value
      } catch (err) {
        // corrupted filters? clear them and don't filter anything
        // localStorage.removeItem("crew-schedule-filters");
        await AmplifyAPIWrapper.Put<any>("/me/settings", {
          crewScheduleFilters: null,
        });
      }
    }, 200),
    []
  );

  // Fires off a debounced function to retrieve saved crew schedule display options and apply them for this window.
  useEffect(() => {
    if (id && dispatch) getSavedDisplayOptions(id, dispatch, appSettings);
  }, [id, dispatch, getSavedDisplayOptions, appSettings]);

  // a handler to pass into the crew schedule header
  const selectCrewPosition = (position: IPositionOptions): void => {
    setCrewPosition(position);
  };

  //If the window closes -> remove the filtered crew from redux
  useEffect(() => {
    // returned function will be called on component unmount
    return () => {
      dispatch(removeFilteredCrew([], id));
      //remove the window properites
      dispatch(removeWindowProperties(id));
      //clear out the violations in redux
      dispatch(clearViolationsFromRedux());
    };
  }, [dispatch, id]);

  const getCrewAction = useCallback(async () => {
    const fetchedCrew: ICrewMember[] = await getCrew();
    dispatch(gotCrewAction(fetchedCrew));
  }, [dispatch]);

  const getData = useCallback(
    (startTime: number, endTime: number) => {
      dispatch(getCrewScheduleData(startTime, endTime, crewScheduleFetchIntervalDays));
    },
    [dispatch, crewScheduleFetchIntervalDays]
  );

  useEffect(() => {
    if (!crew.length && !!getCrewAction) {
      getCrewAction();
    }
  }, [getCrewAction, crew]);

  useEffect(() => {
    if (_.isEmpty(timeIntervalLoaded) && startOfMonth && endOfMonth) {
      getData(startOfMonth, endOfMonth);
    }
  }, [startOfMonth, endOfMonth, getData, timeIntervalLoaded, getCrewAction]);

  /** APPLY DISPLAY FILTERS TO CREW */
  //!! IMPORTANT Filtered crew are no longer applied from redux, just set.
  const filteredCrew = useMemo(() => {
    const crewList = crew ?? [];

    if (selectedBidPackage?.bidPackage?._id) {
      const crewIdsInBidPackage = selectedBidPackage?.crewBids?.map((c) => c?.userId) ?? [];
      const crewInBidPackage = crewList.filter((c) => crewIdsInBidPackage?.includes(c?._id));
      // SORT
      const sortedCrewInBidPackage = crewInBidPackage.sort(sortCrewMembersByPositionThenSeniority);
      return sortedCrewInBidPackage;
    } else {
      if (searchedCrew?.length > 0) {
        return searchedCrew
          .map((searchedCrewId: string) => crewList.find((c: ICrewMember) => c._id === searchedCrewId))
          .filter((c): c is ICrewMember => Boolean(c))
          .sort(sortCrewMembersByPositionThenSeniority);
      }

      const updatedFilteredCrew = crewList.filter((crewMember: ICrewMember) => {
        // FILTER BY BASE ASSIGNMENT
        const baseCheck: boolean =
          crewBase.label === "ALL" || showOnCrewScheduleByBase(crewBase.value.id, crewMember.crewInfo.bases);
        // crewMember.operatingBase = getCrewBaseAtDate(crewMember.crewInfo.bases, new Date()?.valueOf());
        // FILTER BY POSITIONS
        // Match against every position the crew member holds, not just their highest-ranking one.
        // Reducing to the lowest displayOrder meant a crew member holding both FA and one of the
        // ENG-440 positions always collapsed to FA - selecting FT or FC returned nobody at
        // all, because the new positions are seeded at the end of the display order.
        //
        // This also fixes a crash. The old reduce had no initial value, so a crew member with an
        // empty positions array threw "Reduce of empty array with no initial value" from inside a
        // filter callback, taking the whole crew list down. some() returns false instead.
        const positionCheck: boolean =
          crewPosition?.label === "ALL" ||
          (crewMember.crewInfo.positions ?? []).some(
            (position: IPosition) => position._id === crewPosition?.id || position.title === crewPosition?.key
          );
        // FILTER BY GROUPS
        // do not apply filter if crewGroup is undefined or set empty.
        const groupCheck: boolean =
          !crewGroupFilter || crewGroupFilter?.length === 0
            ? true
            : crewMember.crewInfo?.groups?.some((g) => crewGroupFilter.includes(g));

        const showInactiveCrewMember =
          crewGroupFilter?.length && crewGroupFilter.includes("Inactive") ? true : crewMember?.crewInfo?.isActive;

        // Active crew are no longer filtered in redux, do so here before displaying them (only if "Inactive" Group is not selected).
        return baseCheck && positionCheck && groupCheck && showInactiveCrewMember;
      });
      // SORT
      const sortedCrew = updatedFilteredCrew.sort(sortCrewMembersByPositionThenSeniority);
      return sortedCrew;
    }
  }, [
    crew,
    selectedBidPackage?.bidPackage?._id,
    selectedBidPackage?.crewBids,
    searchedCrew,
    crewBase.label,
    crewBase.value.id,
    crewPosition?.label,
    crewPosition?.id,
    crewPosition?.key,
    crewGroupFilter,
  ]);

  // Save filtered and sorted crew in redux for other components to access as needed.
  //We wont set the filtered crew in the above useMemo, as that would cause a bad setState warning.
  useEffect(() => {
    dispatch(setFilteredCrew(filteredCrew, id));
  }, [filteredCrew, dispatch, id, selectedBidPackage?.bidPackage?._id]);

  // Expand/Collapse all rows.
  const allRowsExpanded = useMemo(() => {
    let allExpanded = true;
    if (filteredCrew) {
      filteredCrew.forEach((c) => {
        if (!c.isExpanded) allExpanded = false;
      });
    }
    return allExpanded;
  }, [filteredCrew]);

  const toggleExpandAllCrewRows = useCallback(() => {
    dispatch(expandCollapseAllCrewRows(!allRowsExpanded));
  }, [allRowsExpanded, dispatch]);

  const NFAHeaderHeight = !bidAssignmentActive
    ? BaseTimelineGL?.timelineStart
    : BaseTimelineGL?.timelineStart + nfaRowHeight; // if bid assignment mode active, add a buffer to height of timeline to accomodate selected nfa types.
  return (
    <OCCrewScheduleWindow ref={timelineContainer} container>
      <Grid id="crewSchedHeader" className="crew_sched_header">
        <CrewScheduleHeader
          id={id}
          filteredCrew={filteredCrew}
          timelineContainer={timelineContainer}
          handlePositionSelect={selectCrewPosition}
          currentPosition={crewPosition}
          positionOptions={positionOptions}
          resizeViewport={(value: boolean | string) => {
            setTabValues(value);
          }}
          savedFilters={savedFilters}
          setSavedFilters={setSavedFilters}
          showBidAssignmentTab={bidSettings?.enabled}
          bidAssignModeActive={bidAssignmentActive}
          setActiveEventModal={setActiveEventModal}
        />
      </Grid>
      <Grid id="crewSchedBody" className="crew_sched_body" ref={crewSchedContainer}>
        <Grid id={`${id}-crewList`} className="crew_list" item style={{ width: bidAssignmentActive ? 200 : 120 }}>
          <Grid container direction="column" style={{ height: "100%" }}>
            <Grid
              container
              direction="column"
              wrap="nowrap"
              style={{
                height: NFAHeaderHeight,
              }}
            >
              <Grid container direction="row" alignItems="flex-end">
                {!bidAssignmentActive && (
                  <IconButton
                    className="expand_all_crew_row_btn"
                    onClick={() => toggleExpandAllCrewRows()}
                    size="large"
                  >
                    {allRowsExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
                <Grid item style={{ marginLeft: 10 }}>
                  <TimelineTime
                    container={crewSchedContainer?.current}
                    allowTimezoneControl={true}
                    windowId={id}
                    setFocusOnDate={scrollToDate}
                    // updateCrewBase={true}
                  />
                </Grid>
              </Grid>

              {!!bidAssignmentActive && (
                <SelectField
                  name="bidding_nfa_type_selector"
                  label=""
                  isMulti={true}
                  value={selectedBidNFATypes}
                  onChange={setSelectedBidNFATypes}
                  options={selectedBidPackage?.bidPackage?.nonFlyingAssignments ?? []}
                  getOptionValue={(option: IBidPackageNonFlyingAssignments) => option?.nfaType?._id}
                  getOptionLabel={(option: IBidPackageNonFlyingAssignments) => option?.nfaType?.title}
                  isSearchable={true}
                  hideSelectedValues={false}
                  // multiMode="checkbox"
                  placeholder={
                    selectedBidNFATypes?.length
                      ? selectedBidNFATypes?.map((nfa) => nfa?.nfaType?.title).join(", ")
                      : "NFA Assignment Counts..."
                  }
                />
              )}
            </Grid>

            <CrewList
              crew={filteredCrew}
              id={id}
              crewPosition={crewPosition}
              setCrewListRef={(ref: React.MutableRefObject<HTMLDivElement>) => (crewListRef.current = ref.current)}
              bidRankSettings={bidSettings?.ranks}
              bidAssignmentMode={bidAssignmentActive}
              setActiveEventModal={setActiveEventModal}
              displayPlannedCredit={bidSettings?.displayPlannedCredit}
              displayAssignedNFATypes={bidSettings?.displayAssignedNFATypes ?? []}
            />
          </Grid>
        </Grid>
        <CrewScheduleCanvas
          id={id}
          path={props?.path ?? []}
          crew={filteredCrew}
          crewPairings={crewPairings}
          crewNFAs={crewNFAs}
          crewListRef={crewListRef}
          initialStartTime={startOfMonth}
          initialEndTime={endOfMonth}
          liveLoad={true}
          headerChanged={tabValues}
          dataLoadedCheck={getData}
          setScrollToDate={setScrollToDate}
          setActiveEventModal={setActiveEventModal}
          crewPosition={crewPosition?.key}
          bidRankSettings={bidSettings?.ranks}
          selectedBidPackage={bidAssignmentActive && selectedBidPackage}
          selectedBidNFATypes={selectedBidNFATypes}
          setCloseWarning={setCloseWarning}
        />
      </Grid>
      <CrewScheduleContextMenu
        id={id}
        path={props.path}
        setActiveEventModal={setActiveEventModal}
        containerRef={crewSchedContainer}
      />
      {activeEventModal.type !== EventModalTypes.None && (
        <DialogModalWrapper
          modalType={activeEventModal?.type}
          setActiveEventModal={clearModal}
          modalProps={activeEventModal?.modalProps}
        />
      )}
    </OCCrewScheduleWindow>
  );
};
